import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { colors } from '@/constants/colors';
import {
   AppNotification,
   fetchNotifications,
   fetchUnreadCount,
   markAllNotificationsRead,
   markNotificationRead,
} from '@/api/notification';
import { bellMuted, playChime, setBellMuted, shouldChime } from '@/utils/bellChime';
import IconsLib from '@/utils/IconsLib';
import type { IconType } from '@/utils/icons';

/** One icon per kind of notice, so it reads at a glance (Ray 2026-10-25). */
export const CATEGORY_ICON: Record<string, IconType> = {
   billing: 'wallet-3-line',
   device: 'router-line',
   transfer: 'arrow-left-right-line',
   partner: 'shake-hands-line',
   shop: 'shopping-bag-3-line',
   security: 'shield-check-line',
   system: 'information-line',
};

/**
 * The header bell: unread badge, a dropdown of the latest 20, mark read.
 *
 * Polls the unread count every 30 s (locked decision 2026-05-24 — SSE later).
 * The list is only fetched when the dropdown is open. A rise in the count
 * plays a short chime unless the dropdown's sound switch is off.
 */

const POLL_MS = 30_000;

export function timeAgo(iso: string, now = Date.now()): string {
   const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
   if (s < 60) return 'just now';
   const m = Math.floor(s / 60);
   if (m < 60) return `${m}m ago`;
   const h = Math.floor(m / 60);
   if (h < 24) return `${h}h ago`;
   const d = Math.floor(h / 24);
   return d < 30 ? `${d}d ago` : new Date(iso).toLocaleDateString('en-PH');
}

/** `tab`: the phone bottom menu's "Notifications" tab instead of the header bell (Ray 2026-10-28). */
const NotificationBell: React.FC<{ tab?: boolean }> = ({ tab = false }) => {
   const [open, setOpen] = useState(false);
   const wrapRef = useRef<HTMLDivElement>(null);
   const navigate = useNavigate();
   const qc = useQueryClient();

   const { data: count = 0, isSuccess } = useQuery({
      queryKey: ['notifications', 'unread-count'],
      queryFn: fetchUnreadCount,
      refetchInterval: POLL_MS,
      retry: false,
   });

   const [muted, setMuted] = useState(bellMuted);
   const lastCount = useRef<number | null>(null);
   useEffect(() => {
      if (!isSuccess) return;
      if (shouldChime(lastCount.current, count) && !muted) playChime();
      lastCount.current = count;
   }, [count, isSuccess, muted]);

   const toggleSound = () => {
      setBellMuted(!muted);
      setMuted(!muted);
   };

   const { data: list = [], isLoading } = useQuery({
      queryKey: ['notifications', 'list'],
      queryFn: fetchNotifications,
      enabled: open,
      retry: false,
   });

   // Close on a click anywhere else.
   useEffect(() => {
      if (!open) return;
      const onDown = (e: MouseEvent) => {
         if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
            setOpen(false);
         }
      };
      document.addEventListener('mousedown', onDown);
      return () => document.removeEventListener('mousedown', onDown);
   }, [open]);

   const refresh = () => qc.invalidateQueries({ queryKey: ['notifications'] });

   const onPick = async (n: AppNotification) => {
      setOpen(false);
      const qs = n.link?.params ? `?${new URLSearchParams(n.link.params)}` : '';
      // A shop buyer's order lives on the shop site (smartfi.ph): open it in a
      // new tab — right away, inside the click, or the browser blocks the tab.
      // Only our own sites, never an outside address.
      const external = !!n.link?.path && /^https:\/\/([a-z0-9-]+\.)*smartfi\.ph\//i.test(n.link.path);
      if (external) window.open(`${n.link!.path}${qs}`, '_blank', 'noopener');
      if (!n.read) {
         try {
            await markNotificationRead(n._id);
         } finally {
            refresh();
         }
      }
      if (!external && n.link?.path?.startsWith('/')) navigate(`${n.link.path}${qs}`);
   };

   const onReadAll = async () => {
      try {
         await markAllNotificationsRead();
      } finally {
         refresh();
      }
   };

   return (
      <Wrap ref={wrapRef} $tab={tab}>
         {tab ? (
            <TabButton
               type="button"
               $active={open}
               aria-label={count ? `Notifications, ${count} unread` : 'Notifications'}
               onClick={() => setOpen((o) => !o)}
            >
               <TabIcon>
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                     <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                     <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  {count > 0 && <TabBadge data-testid="bell-badge">{count > 99 ? '99+' : count}</TabBadge>}
               </TabIcon>
               <span>Notifications</span>
            </TabButton>
         ) : (
            <BellButton
               type="button"
               aria-label={count ? `Notifications, ${count} unread` : 'Notifications'}
               title="Notifications"
               onClick={() => setOpen((o) => !o)}
            >
               <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
               </svg>
               {count > 0 && <Badge data-testid="bell-badge">{count > 99 ? '99+' : count}</Badge>}
            </BellButton>
         )}

         {open && tab && <SheetScrim data-testid="bell-scrim" onClick={() => setOpen(false)} />}
         {open && (
            <Panel role="dialog" aria-label="Notifications" $tab={tab} data-sheet={tab ? '' : undefined}>
               {tab && <SheetHandle aria-hidden="true" />}
               <PanelHead>
                  <strong>Notifications</strong>
                  <HeadActions>
                     <TextButton
                        type="button"
                        onClick={toggleSound}
                        aria-pressed={!muted}
                        title={muted ? 'New notifications are silent' : 'A chime plays when a new notification arrives'}
                     >
                        {muted ? 'Sound off' : 'Sound on'}
                     </TextButton>
                     {count > 0 && (
                        <TextButton type="button" onClick={onReadAll}>
                           Mark all as read
                        </TextButton>
                     )}
                  </HeadActions>
               </PanelHead>
               <List>
                  {isLoading && <Empty>Loading…</Empty>}
                  {!isLoading && list.length === 0 && <Empty>No notifications yet.</Empty>}
                  {list.map((n) => (
                     <Item
                        key={n._id}
                        type="button"
                        $unread={!n.read}
                        onClick={() => onPick(n)}
                     >
                        <CatIcon $severity={n.severity} data-testid={`notice-icon-${n.category}`} aria-hidden="true">
                           <IconsLib icon={CATEGORY_ICON[n.category] ?? 'information-line'} size="17px" color={SEVERITY_COLOR[n.severity]} />
                           {!n.read && <UnreadDot $severity={n.severity} />}
                        </CatIcon>
                        <ItemText>
                           <ItemTitle $unread={!n.read} data-unread={!n.read}>
                              {n.title}
                           </ItemTitle>
                           {n.body && <ItemBody>{n.body}</ItemBody>}
                           <ItemTime>{timeAgo(n.createdAt)}</ItemTime>
                        </ItemText>
                     </Item>
                  ))}
               </List>
            </Panel>
         )}
      </Wrap>
   );
};

export default NotificationBell;

const SEVERITY_COLOR: Record<AppNotification['severity'], string> = {
   info: colors.PRIMARY,
   success: '#16a34a',
   warning: '#f59e0b',
   error: colors.RED,
};

const Wrap = styled.div<{ $tab: boolean }>`
   position: relative;
   ${({ $tab }) => ($tab ? 'display: flex;' : '')}
`;
const TabButton = styled.button<{ $active: boolean }>`
   flex: 1;
   background: none;
   border: none;
   outline: none;
   cursor: pointer;
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   gap: 2px;
   padding: 8px 4px;
   font-size: 11px;
   font-weight: ${({ $active }) => ($active ? 600 : 500)};
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY1)};
   &:active {
      background: ${colors.GRAY14};
   }
`;
const TabIcon = styled.span`
   position: relative;
   display: inline-flex;
`;
const TabBadge = styled.span`
   position: absolute;
   top: -5px;
   right: -9px;
   min-width: 16px;
   height: 16px;
   padding: 0 4px;
   border-radius: 999px;
   display: grid;
   place-items: center;
   font-size: 10px;
   font-weight: 700;
   color: ${colors.WHITE};
   background: ${colors.RED};
`;
const BellButton = styled.button`
   position: relative;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   width: 34px;
   height: 34px;
   border: none;
   border-radius: 8px;
   background: transparent;
   color: var(--app-text);
   cursor: pointer;
   &:hover {
      background: ${colors.GRAY13};
   }
`;
const Badge = styled.span`
   position: absolute;
   top: 2px;
   right: 1px;
   min-width: 16px;
   height: 16px;
   padding: 0 4px;
   border-radius: 8px;
   background: ${colors.RED};
   color: #fff;
   font-size: 10px;
   font-weight: 700;
   line-height: 16px;
   text-align: center;
`;
const Panel = styled.div<{ $tab?: boolean }>`
   position: absolute;
   top: 42px;
   right: 0;
   z-index: 1000;
   width: 360px;
   max-width: calc(100vw - 24px);
   background: var(--app-surface);
   border: 1px solid ${colors.GRAY13};
   border-radius: 10px;
   box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
   overflow: hidden;
   @media (max-width: 768px) {
      position: fixed;
      top: 52px;
      right: 12px;
   }
   /* From the phone's bottom menu: a sheet that slides up from the bottom, like Menu (Ray 2026-10-28: no floating panel on phones). */
   /* "&&" outranks the phone rule above (top: 52px) so the sheet really sits at the bottom. */
   ${({ $tab }) =>
      $tab
         ? `&& { position: fixed; top: auto; left: 0; right: 0; bottom: 0; width: auto; max-width: none; max-height: 80vh; z-index: 1001;
            display: flex; flex-direction: column; border: 0; border-radius: 18px 18px 0 0; box-shadow: 0 -8px 30px rgba(0, 0, 0, 0.2);
            padding-bottom: env(safe-area-inset-bottom, 0px); animation: bell-sheet-up 0.2s ease-out; }`
         : ''}
   @keyframes bell-sheet-up { from { transform: translateY(24px); opacity: 0.6; } to { transform: none; opacity: 1; } }
`;
const SheetScrim = styled.div`
   position: fixed;
   inset: 0;
   z-index: 1000;
   background: rgba(0, 0, 0, 0.4);
`;
const SheetHandle = styled.div`
   width: 40px;
   height: 4px;
   border-radius: 999px;
   background: ${colors.GRAY12};
   margin: 8px auto 0;
`;
const PanelHead = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   padding: 10px 14px;
   border-bottom: 1px solid ${colors.GRAY13};
   font-size: 14px;
`;
const HeadActions = styled.div`
   display: flex;
   gap: 12px;
`;
const TextButton = styled.button`
   border: none;
   background: none;
   color: ${colors.PRIMARY};
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
`;
const List = styled.div`
   max-height: 420px;
   [data-sheet] & { max-height: none; flex: 1; min-height: 0; }
   overflow-y: auto;
`;
const Empty = styled.p`
   padding: 24px 14px;
   text-align: center;
   color: var(--app-text-muted);
   font-size: 13px;
`;
const Item = styled.button<{ $unread: boolean }>`
   display: flex;
   gap: 10px;
   width: 100%;
   padding: 10px 14px;
   border: none;
   border-bottom: 1px solid ${colors.GRAY13};
   background: ${(p) => (p.$unread ? `${colors.PRIMARY}0d` : 'transparent')};
   color: var(--app-text);
   text-align: left;
   cursor: pointer;
   &:hover {
      background: ${colors.GRAY14};
   }
`;
const CatIcon = styled.span<{ $severity: AppNotification['severity'] }>`
   position: relative;
   flex: 0 0 32px;
   height: 32px;
   border-radius: 50%;
   display: grid;
   place-items: center;
   background: ${(p) => `${SEVERITY_COLOR[p.$severity]}1f`};
`;
const UnreadDot = styled.span<{ $severity: AppNotification['severity'] }>`
   position: absolute;
   top: -1px;
   right: -1px;
   width: 9px;
   height: 9px;
   border-radius: 50%;
   background: ${(p) => SEVERITY_COLOR[p.$severity]};
   box-shadow: 0 0 0 2px var(--app-surface, #fff);
`;
const ItemText = styled.div`
   min-width: 0;
`;
// Bold only while unread — once opened, the title relaxes (Ray 2026-10-26).
const ItemTitle = styled.p<{ $unread: boolean }>`
   font-size: 13px;
   font-weight: ${(p) => (p.$unread ? 700 : 500)};
   opacity: ${(p) => (p.$unread ? 1 : 0.8)};
`;
const ItemBody = styled.p`
   margin-top: 2px;
   font-size: 12px;
   color: var(--app-text-dim);
`;
const ItemTime = styled.p`
   margin-top: 4px;
   font-size: 11px;
   color: var(--app-text-muted);
`;
